import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { isNil, noop } from 'lodash';

import { useCms } from '~/components/hooks/useCms';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { usePaymentsLimits } from '~/components/hooks/usePaymentLimits';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import useOrganization from '~/components/OrganizationContext';

import { reportAxiosError } from '../../../Utils';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '../../core';
import {
  doesLimitCoverAmount,
  getPolicyExposureLimit,
  getPolicyExposureLimitPerAccidentIfExists,
} from '../ExposureUtils';

import MakePaymentRequestDialog from './MakePaymentRequestDialog/MakePaymentRequestDialog';
import PaymentRequestApprovalDialog from './PaymentRequestApprovalDialog';
import PaymentRequestCancelDialog from './PaymentRequestCancelDialog';
import PaymentRequestDecisionDialog from './PaymentRequestDecisionDialog';

export const PaymentRequestContainer = ({
  claim,
  exposure,
  payableWithReserve,
  payableType,
  paymentRequest,
  cardDialogProps,
  onClose: onCloseProp,
  onUpdate,
  setIsSubmitting = noop,
  overridePaymentRequestStore,
  defaultAmount,
  overrideInitialValues,
  forceManualDataInit = false,
  paymentSuggestion,
  AdditionalDialogHeader = <React.Fragment />,
  additionalPayload,
  enableFutureInServiceDates = false,
  refreshInBackground = false,
}) => {
  // Coalesce with ?? so both an omitted (undefined) and an explicitly null onClose collapse to a
  // noop for every consumer that always invokes it: the post-submit continuations below, and the
  // decision / approval / cancel dialogs, whose onClose / onCancel are required props they call
  // unconditionally. Do NOT hand this noop to the new-payment dialog: it gates its close X purely
  // on onCancel being truthy, so a caller that deliberately omits onClose — a card rendered inside
  // another card, which is closed by its parent — would get an X that only resets the form,
  // dropping the payment-request-store payee fields out of Formik values while the store still
  // lists them, which throws on the next render.
  const onClose = onCloseProp ?? noop;

  const { canCurrentUserApprovePayment } = usePaymentsConfiguration();
  const { isDelegatedAuthorityEnabled } = useOrganization();
  const { user } = useCms();

  const userHasPermissionsOfacOverride = useHasPermission({
    action: PERMISSION_ACTIONS.OFAC_OVERRIDE,
    verb: PERMISSION_VERBS.WRITE,
  });

  const canApproveAsManagerOrDesignatedApprover = paymentRequest
    ? canCurrentUserApprovePayment(
        paymentRequest.requester_user?.id,
        paymentRequest.was_ofac_hit_found,
        paymentRequest.was_ofac_hit_approved,
        userHasPermissionsOfacOverride
      )
    : false;
  // Under delegated authority, approving is level-based: fetch the user's remaining
  // authority (the /limit endpoint is delegated-authority-aware) and let a covering
  // limit open the approval dialog. Round-robin routes an over-authority payment to a
  // covering peer regardless of the approval screen, so a covering level entitles even
  // when the screen is on (mirrors the backend gate). Skipped when the legacy manager /
  // designated-approver gate already passes, and for users a covering limit could never
  // entitle anyway (the requester themselves, or an unapproved OFAC hit without override
  // permission) so their cancel-only dialog renders without waiting for the fetch.
  const isBlockedRegardlessOfAuthority =
    !paymentRequest ||
    paymentRequest.requester_user?.id === user?.id ||
    Boolean(
      paymentRequest.was_ofac_hit_found && !paymentRequest.was_ofac_hit_approved && !userHasPermissionsOfacOverride
    );
  const shouldCheckDelegatedAuthorityCoverage = Boolean(
    isDelegatedAuthorityEnabled &&
      paymentRequest &&
      paymentRequest.request_status === 'pending' &&
      !canApproveAsManagerOrDesignatedApprover &&
      !isBlockedRegardlessOfAuthority
  );
  const { userExposureLimit, isUserExposureLimitError } = usePaymentsLimits(
    claim,
    exposure,
    payableType,
    'payment',
    shouldCheckDelegatedAuthorityCoverage
  );
  const hasCoveringDelegatedAuthority =
    shouldCheckDelegatedAuthorityCoverage &&
    !isNil(userExposureLimit) &&
    doesLimitCoverAmount(userExposureLimit, paymentRequest.request_amount);

  const policyExposureLimit = getPolicyExposureLimit(claim, exposure);
  const policyExposureLimitPerAccident = getPolicyExposureLimitPerAccidentIfExists(exposure);

  async function _handleSubmit(path, values, { skipReportAxiosError = false, backgroundRefresh = false } = {}) {
    setIsSubmitting(true);

    const valuesWithSuggestion = paymentSuggestion ? { ...values, payment_suggestion: paymentSuggestion } : values;
    // Merge additionalPayload if provided (e.g., line_item_ids)
    const finalValues = additionalPayload ? { ...valuesWithSuggestion, ...additionalPayload } : valuesWithSuggestion;

    const reportSubmitError = (error) => {
      // Surface the failure and reset the dialog's submit state so the user can retry.
      // skipReportAxiosError lets the caller own its error handling (e.g. MakePaymentRequestDialog).
      if (!skipReportAxiosError) {
        reportAxiosError(error);
        setIsSubmitting(false);
      }
    };

    let res;
    try {
      res = await axios.post(path, finalValues);
    } catch (error) {
      reportSubmitError(error);
      throw error;
    }

    if (backgroundRefresh) {
      // Close the dialog as soon as the request succeeds and let the list/claim refresh run
      // asynchronously in the background, instead of blocking the dialog open until the
      // (potentially heavy) refresh resolves. The dialog is already gone, so a failed refresh is
      // only reported (honoring skipReportAxiosError) — no submit-state reset on a closed dialog.
      // onClose is coalesced to noop above, so it is always safe to call here.
      onClose();
      Promise.resolve(onUpdate(res.data)).catch((error) => {
        if (!skipReportAxiosError) {
          reportAxiosError(error);
        }
      });
      return;
    }

    try {
      await onUpdate(res.data);
    } catch (error) {
      reportSubmitError(error);
      throw error;
    }
    onClose();
  }

  const payableRequestsUrl = exposure.isGeneralExpenses
    ? `/api/v1/claims/${claim.id}/general_expenses/payment_requests`
    : `/api/v1/claims/${claim.id}/exposures/${exposure.id}/${payableType}/payment_requests`;

  function handleSubmitPaymentRequest(values, skipReportAxiosError = false) {
    return _handleSubmit(payableRequestsUrl, values, { skipReportAxiosError });
  }

  function handleCancelPaymentRequest(values) {
    return _handleSubmit(
      `${payableRequestsUrl}/${paymentRequest.id}/decide`,
      { ...values, decision: 'cancel' },
      { backgroundRefresh: refreshInBackground }
    );
  }

  function handleDecidePaymentRequest(values) {
    return _handleSubmit(`${payableRequestsUrl}/${paymentRequest.id}/decide`, values, {
      backgroundRefresh: refreshInBackground,
    });
  }

  function getDialog() {
    if (!paymentRequest) {
      return (
        <MakePaymentRequestDialog
          cardDialogProps={cardDialogProps}
          onSubmitPaymentRequest={handleSubmitPaymentRequest}
          onCancel={onCloseProp}
          claim={claim}
          exposure={exposure}
          payableWithReserve={payableWithReserve}
          payableType={payableType}
          policyExposureLimit={policyExposureLimit}
          policyExposureLimitPerAccident={policyExposureLimitPerAccident}
          overridePaymentRequestStore={overridePaymentRequestStore}
          defaultAmount={defaultAmount}
          overrideInitialValues={overrideInitialValues}
          forceManualDataInit={forceManualDataInit}
          AdditionalDialogHeader={AdditionalDialogHeader}
          enableFutureInServiceDates={enableFutureInServiceDates}
        />
      );
    }

    // paymentRequest
    if (paymentRequest.request_status !== 'pending') {
      return (
        <PaymentRequestDecisionDialog
          cardDialogProps={cardDialogProps}
          payableWithReserve={payableWithReserve}
          paymentRequest={paymentRequest}
          payableType={payableType}
          onClose={onClose}
          claim={claim}
          exposure={exposure}
          AdditionalDialogHeader={AdditionalDialogHeader}
        />
      );
    }

    // payment_request is pending
    if (shouldCheckDelegatedAuthorityCoverage && isNil(userExposureLimit) && !isUserExposureLimitError) {
      // The remaining-authority fetch decides which dialog to render; rendering the
      // cancel-only dialog while it is in flight would flash the wrong dialog. If the
      // fetch failed, fall through with no coverage so the cancel dialog still renders.
      return null;
    }

    if (
      canCurrentUserApprovePayment(
        paymentRequest.requester_user?.id,
        paymentRequest.was_ofac_hit_found,
        paymentRequest.was_ofac_hit_approved,
        userHasPermissionsOfacOverride,
        hasCoveringDelegatedAuthority
      )
    ) {
      return (
        <PaymentRequestApprovalDialog
          cardDialogProps={cardDialogProps}
          payableWithReserve={payableWithReserve}
          payableType={payableType}
          paymentRequest={paymentRequest}
          onDecidePaymentRequest={handleDecidePaymentRequest}
          onCancel={onClose}
          claim={claim}
          exposure={exposure}
          AdditionalDialogHeader={AdditionalDialogHeader}
        />
      );
    } else {
      return (
        <PaymentRequestCancelDialog
          cardDialogProps={cardDialogProps}
          payableWithReserve={payableWithReserve}
          payableType={payableType}
          paymentRequest={paymentRequest}
          onCancelPaymentRequest={handleCancelPaymentRequest}
          onCancel={onClose}
          claim={claim}
          exposure={exposure}
          AdditionalDialogHeader={AdditionalDialogHeader}
        />
      );
    }
  }

  return <>{getDialog()}</>;
};

const propTypes = {
  claim: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  setIsSubmitting: PropTypes.func,
  exposure: PropTypes.object.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  payableType: PropTypes.string.isRequired,
  paymentRequest: PropTypes.object,
  cardDialogProps: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  overridePaymentRequestStore: PropTypes.array,
  defaultAmount: PropTypes.number,
  overrideInitialValues: PropTypes.object,
  forceManualDataInit: PropTypes.bool,
  paymentSuggestion: PropTypes.object,
  AdditionalDialogHeader: PropTypes.node,
  additionalPayload: PropTypes.object,
  enableFutureInServiceDates: PropTypes.bool,
  refreshInBackground: PropTypes.bool,
};

PaymentRequestContainer.propTypes = propTypes;

export const MinimizedPaymentRequestContainer = ({ onMinimized, disableMinimized, open, cardDialogProps, ...rest }) => (
  <PaymentRequestContainer {...rest} cardDialogProps={{ ...cardDialogProps, onMinimized, disableMinimized, open }} />
);

MinimizedPaymentRequestContainer.propTypes = {
  ...propTypes,
  onMinimized: PropTypes.func,
  disableMinimized: PropTypes.bool,
  open: PropTypes.bool,
};

export default PaymentRequestContainer;
